// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link, RouteComponentProps } from 'react-router-dom';

// Components
import classNames from 'classnames';
import { SecondaryButton } from '../buttons';
import { PrimaryNavigation } from '../navigation';
import DelegateBanner from '../../containers/auth/DelegateBanner';
import SkipContentLink from '../misc/SkipContentLink';
import { AnnouncementBanner, AnnouncementBell } from '../../containers/announcements';
import UserDropdown from '../navigation/UserDropdown';

// Images
import { ReactComponent as CapintelLogo } from '../../../images/capintel-logo.svg?react';
import { ReactComponent as CapintelLogoSmall } from '../../../images/capintel-logo-small.svg?react';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { NAV_PATHS } from '../../../utils/constants';
import { userHasPermission, PERMISSIONS, userHasAdminPermission } from '../../../utils/permissions';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { CurrentContact } from '../../../redux/modules/types/contacts';
import { getRecentComparisons } from '../../../redux/modules/comparison';
import { setLocale } from '../../../redux/modules/intl';
import { selectDelegateMode } from '../../../redux/modules/auth/selectors';
import { logOpenHelpCenter } from '../../../services/usage-log';

// Type
import AppMode from '@/types/app-mode';
import Locale from '@/types/locale';
import NavigationItem from '@/types/navigation-item';
import Nullable from '@/types/nullable';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';

type StateProps = {
  contact?: Nullable<CurrentContact>;
  delegateMode?: boolean;
  locale: Locale;
  user?: Nullable<User>;
};

type DispatchProps = {
  getRecentComparisons: (query?: string, nextToken?: string) => Promise<ReducedComparison[]>;
  setLocale: (newLocale: Locale) => void;
  logOpenHelpCenter: () => Promise<void>;
};

type Props = RouteComponentProps & DispatchProps & StateProps;

class Header extends Component<Props> {
  openHelpLink = () => {
    this.props.logOpenHelpCenter();
    window.open(`${import.meta.env.REACT_APP_HELP_SITE_URL}/${this.props.locale.toLocaleLowerCase()}`, '_blank');
  };

  getContactHeaderTitle = () => {
    const { user } = this.props;

    if (user && userHasPermission(user, PERMISSIONS.BMO_GIF)) {
      return formatMessage('SiteNavigation.Clients');
    }

    return formatMessage('SiteNavigation.Contacts');
  };

  getLeftMenuItems = (user: User): NavigationItem[] => {
    const { contact, delegateMode } = this.props;

    const showModelsBasedOnPermission = () => {
      if (!userHasPermission(user, PERMISSIONS.USER_MODELS) && !userHasPermission(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
        return NAV_PATHS.CUSTOM_BENCHMARKS;
      }

      if (!userHasPermission(user, PERMISSIONS.USER_MODELS)) {
        return NAV_PATHS.ENTERPRISE_MODELS;
      }

      return NAV_PATHS.MODELS;
    };

    const items: NavigationItem[] = [
      {
        label: formatMessage('SiteNavigation.Comparisons'),
        value: NAV_PATHS.COMPARISONS,
        hidden: !userHasPermission(user, PERMISSIONS.SECTION_COMPARISONS),
      },
      {
        label: this.getContactHeaderTitle(),
        value:
          user.appConfig.mode === AppMode.CONTACT
            ? NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contact != null && contact.contact._id != null ? contact.contact._id : '')
            : NAV_PATHS.CONTACTS,
        hidden: !userHasPermission(user, PERMISSIONS.SECTION_CONTACTS),
      },
      {
        label: formatMessage('SiteNavigation.Search'),
        value: NAV_PATHS.SEARCH,
        hidden: !userHasPermission(user, PERMISSIONS.SECTION_SEARCH),
      },
      {
        label: formatMessage('SiteNavigation.Models'),
        value: showModelsBasedOnPermission(),
        hidden: !userHasPermission(user, PERMISSIONS.SECTION_MODELS),
      },
      {
        label: formatMessage('SiteNavigation.Insights'),
        value: NAV_PATHS.INSIGHTS,
        hidden: !userHasPermission(user, PERMISSIONS.SECTION_INSIGHTS),
      },
      {
        label: formatMessage('SiteNavigation.Admin'),
        value: NAV_PATHS.ADMIN,
        hidden: !userHasAdminPermission(user) || delegateMode,
      },
    ];

    return items.filter(i => !i.hidden);
  };

  render() {
    const { user, contact } = this.props;

    if (!user) {
      return null;
    }

    let home: string = NAV_PATHS.COMPARISONS;
    if (user.appConfig.mode === AppMode.CONTACT) {
      const contactId: string | undefined = contact != null ? contact.contact._id : undefined;
      if (contactId != null) {
        home = NAV_PATHS.CONTACT_DETAILS.replace(':contactId', contactId);
      } else {
        home = NAV_PATHS.CONTACTS;
      }
    }

    return (
      <>
        <SkipContentLink />
        <DelegateBanner />
        <AnnouncementBanner />

        <div className="header d-flex justify-content-between">
          <div className="d-flex align-items-center header-left-items">
            <Link to={home} aria-label="CapIntel">
              <CapintelLogo className="capintel-logo logo-large" />
              <CapintelLogoSmall className="capintel-logo logo-small" />
            </Link>

            <PrimaryNavigation items={this.getLeftMenuItems(user)} />
          </div>
          <div className="d-flex align-items-center header-right-items">
            <SecondaryButton
              className={classNames('header-help-button', 'navigation-item')}
              noBackground
              icon="round-chat-interrogation"
              dark
              onClick={this.openHelpLink}
            >
              <FormattedMessage id="SiteNavigation.Help" />
            </SecondaryButton>
            {userHasPermission(user, PERMISSIONS.EXTERNAL_NOTIFICATION_CENTER) && (
              <SecondaryButton className={classNames('notification-center-trigger', 'navigation-item')} noBackground icon="notification-bell" dark />
            )}
            {userHasPermission(user, PERMISSIONS.NOTIFICATION_CENTER) && <AnnouncementBell />}
            <UserDropdown user={user} history={this.props.history} location={this.props.location} match={this.props.match} />
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contact: state.contacts.current,
  delegateMode: selectDelegateMode(state),
  locale: state.intl.locale,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getRecentComparisons,
  setLocale,
  logOpenHelpCenter,
})(Header);
